import type { HomepageAudio } from "./Audio";
import { requiredChild, requiredElement } from "./Dom";
import { type LogoKey, logoKeys } from "./Types";
import { registerKey, type InputId, type KeyControls } from "./KeyboardKey";

export function initKeyboardLogo(
	audio: HomepageAudio,
	onKey: (key: LogoKey) => void,
): void {
	const activePointerKeys = new Map<InputId, KeyControls>();
	const activeTouchKeys = new Map<InputId, KeyControls>();
	const nativeTouchPointers = new Map<number, Pick<KeyControls, "release">>();
	const hasNativeTouchEvents = "ontouchstart" in window;

	function releaseAllKeys(playReleaseSound: boolean): void {
		const releases = new Set(
			[
				...activePointerKeys.values(),
				...activeTouchKeys.values(),
				...nativeTouchPointers.values(),
			].map((input) => input.release),
		);
		for (const release of releases) {
			release(playReleaseSound);
		}
		nativeTouchPointers.clear();
	}

	window.addEventListener(
		"pointermove",
		(event) => {
			activePointerKeys
				.get(event.pointerId)
				?.move(event.clientX, event.clientY);
		},
		true,
	);
	window.addEventListener(
		"pointerup",
		(event) => {
			activePointerKeys.get(event.pointerId)?.release(true);
			nativeTouchPointers.get(event.pointerId)?.release(true);
			nativeTouchPointers.delete(event.pointerId);
		},
		true,
	);
	window.addEventListener(
		"pointercancel",
		(event) => {
			activePointerKeys.get(event.pointerId)?.release(false);
			nativeTouchPointers.get(event.pointerId)?.release(false);
			nativeTouchPointers.delete(event.pointerId);
		},
		true,
	);
	window.addEventListener(
		"touchmove",
		(event) => {
			for (const touch of event.changedTouches) {
				activeTouchKeys
					.get(touch.identifier)
					?.move(touch.clientX, touch.clientY);
			}
		},
		{ capture: true, passive: true },
	);
	window.addEventListener(
		"touchend",
		(event) => {
			for (const touch of event.changedTouches) {
				activeTouchKeys.get(touch.identifier)?.release(true);
			}
		},
		{ capture: true, passive: true },
	);
	window.addEventListener(
		"touchcancel",
		(event) => {
			for (const touch of event.changedTouches) {
				activeTouchKeys.get(touch.identifier)?.release(false);
			}
		},
		{ capture: true, passive: true },
	);
	window.addEventListener("blur", () => releaseAllKeys(false));
	window.addEventListener("pagehide", () => releaseAllKeys(false));
	document.addEventListener("visibilitychange", () => {
		if (document.hidden) {
			releaseAllKeys(false);
		}
	});

	const logo = requiredChild<SVGSVGElement>(document, ".homepage > .hero");
	logo.setAttribute("role", "group");
	logo.setAttribute("aria-label", "Interactive Samwho keyboard logo");
	const preloadAudio = (): void => {
		audio.preloadKeys();
		audio.preloadEffects();
	};

	logo.addEventListener("pointerenter", (event) => {
		if (event.pointerType === "mouse") {
			preloadAudio();
		}
	});
	logo.addEventListener("pointerdown", preloadAudio, {
		once: true,
		capture: true,
	});
	logo.addEventListener("focusin", preloadAudio, { once: true });
	for (const key of logoKeys) {
		logo.append(requiredElement<SVGGElement>(key));
		registerKey(key, audio, onKey, {
			activePointerKeys,
			activeTouchKeys,
			nativeTouchPointers,
			hasNativeTouchEvents,
		});
	}
}
